import React, { useId, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { MessageSquare, Pencil, Trash2, Check, X } from "lucide-react";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";

interface CandidateNotesThreadProps {
  organizationBusinessUnitId: string;
  userId: string;
  /** The authenticated user's id — used to enable edit/delete on their own notes. */
  currentUserId: string;
  /** If true, the current user is an admin and may edit/delete any note. */
  isAdmin: boolean;
}

export function CandidateNotesThread({
  organizationBusinessUnitId,
  userId,
  currentUserId,
  isAdmin,
}: CandidateNotesThreadProps) {
  const trpc = useTRPC();
  const draftInputId = useId();
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const listOptions = trpc.jobPost.listCandidateNotes.queryOptions({
    organizationBusinessUnitId,
    userId,
  });

  const { data: notes, isLoading } = useQuery(listOptions);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: listOptions.queryKey }),
      queryClient.invalidateQueries({
        queryKey: trpc.jobPost.candidateNoteSummaries.queryKey(),
      }),
    ]);
  };

  const createMutation = useMutation(
    trpc.jobPost.createCandidateNote.mutationOptions({
      onSuccess: async () => {
        setDraft("");
        await invalidate();
      },
      onError: (err) => toast.error(err.message || "Failed to save note"),
    }),
  );

  const updateMutation = useMutation(
    trpc.jobPost.updateCandidateNote.mutationOptions({
      onSuccess: async () => {
        setEditingId(null);
        setEditDraft("");
        await invalidate();
      },
      onError: (err) => toast.error(err.message || "Failed to update note"),
    }),
  );

  const deleteMutation = useMutation(
    trpc.jobPost.deleteCandidateNote.mutationOptions({
      onSuccess: async () => {
        await invalidate();
      },
      onError: (err) => toast.error(err.message || "Failed to delete note"),
    }),
  );

  const handleCreate = () => {
    const body = draft.trim();
    if (!body) {
      return;
    }
    createMutation.mutate({
      organizationBusinessUnitId,
      userId,
      body,
    });
  };

  const handleUpdate = (id: string) => {
    const body = editDraft.trim();
    if (!body) {
      return;
    }
    updateMutation.mutate({
      organizationBusinessUnitId,
      id,
      body,
    });
  };

  const handleDelete = (id: string) => {
    if (!confirm("Delete this note? This cannot be undone.")) {
      return;
    }
    deleteMutation.mutate({ organizationBusinessUnitId, id });
  };

  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <MessageSquare size={16} className="shrink-0 text-primary" />
        <span className="text-sm font-semibold text-slate-900">
          Internal notes
        </span>
        <span className="text-xs text-slate-500">
          · visible to your team only
        </span>
      </div>

      <div className="min-w-0 space-y-3 p-3 sm:p-4">
        {isLoading ? (
          <p className="py-3 text-sm text-slate-500">Loading notes...</p>
        ) : notes && notes.length > 0 ? (
          notes.map((note) => {
            const isEditing = editingId === note.id;
            const canMutate = isAdmin || note.authorUserId === currentUserId;
            const authorName =
              `${note.author?.firstName ?? ""} ${note.author?.lastName ?? ""}`.trim() ||
              "Unknown";
            return (
              <div
                key={note.id}
                className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4"
              >
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 text-xs leading-6 text-slate-500 [overflow-wrap:anywhere]">
                    <span className="font-medium text-slate-700">
                      {authorName}
                    </span>
                    {" · "}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="rounded-sm text-left underline decoration-dotted decoration-slate-400 underline-offset-2 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          {formatDistanceToNow(new Date(note.createdAt), {
                            addSuffix: true,
                          })}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] rounded-xl border-slate-200 bg-white px-4 py-3 text-xs shadow-lg">
                        <div className="font-medium text-slate-700">
                          {format(new Date(note.createdAt), "PPpp")}
                        </div>
                        {note.updatedAt &&
                          new Date(note.updatedAt).getTime() !==
                            new Date(note.createdAt).getTime() && (
                            <div className="text-slate-500 mt-1">
                              Edited {format(new Date(note.updatedAt), "PPpp")}
                            </div>
                          )}
                      </PopoverContent>
                    </Popover>
                    {note.updatedAt &&
                      new Date(note.updatedAt).getTime() !==
                        new Date(note.createdAt).getTime() && (
                        <span className="text-slate-400"> (edited)</span>
                      )}
                  </div>
                  {canMutate && !isEditing && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        className="size-12 shrink-0 px-0"
                        variant="outline"
                        aria-label="Edit note"
                        onClick={() => {
                          setEditingId(note.id);
                          setEditDraft(note.body);
                        }}
                      >
                        <Pencil aria-hidden="true" size={16} />
                      </Button>
                      <Button
                        type="button"
                        className="size-12 shrink-0 border-red-200 px-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                        variant="outline"
                        aria-label="Delete note"
                        onClick={() => handleDelete(note.id)}
                      >
                        <Trash2 aria-hidden="true" size={16} />
                      </Button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      aria-label="Edit internal note"
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={3}
                      className="text-sm"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        className="size-12 px-0"
                        variant="outline"
                        aria-label="Cancel edit"
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft("");
                        }}
                      >
                        <X aria-hidden="true" size={16} />
                      </Button>
                      <Button
                        type="button"
                        className="size-12 px-0"
                        aria-label="Save edit"
                        onClick={() => handleUpdate(note.id)}
                        disabled={updateMutation.isPending || !editDraft.trim()}
                      >
                        <Check aria-hidden="true" size={16} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 [overflow-wrap:anywhere]">
                    {note.body}
                  </p>
                )}
              </div>
            );
          })
        ) : (
          <p className="py-4 text-center text-sm text-slate-500">
            No internal notes yet.
          </p>
        )}
      </div>

      <div className="space-y-3 border-t border-slate-200 p-3 sm:p-4">
        <Label htmlFor={draftInputId}>Add an internal note</Label>
        <Textarea
          id={draftInputId}
          placeholder="Add an internal note about this candidate for this role..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          className="text-sm"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            className="w-full sm:w-auto"
            onClick={handleCreate}
            disabled={createMutation.isPending || !draft.trim()}
          >
            Add note
          </Button>
        </div>
      </div>
    </div>
  );
}

interface CandidateNotesDialogProps {
  candidate: { userId: string; name: string };
  organizationBusinessUnitId: string;
  currentUserId: string;
  isAdmin: boolean;
  onClose: () => void;
}

export function CandidateNotesDialog({
  candidate,
  organizationBusinessUnitId,
  currentUserId,
  isAdmin,
  onClose,
}: CandidateNotesDialogProps) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="!w-[calc(100vw-2rem)] !max-w-2xl !max-h-[calc(100dvh-2rem)] grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-2xl border-slate-200 bg-white p-0 text-slate-900 sm:rounded-2xl">
        <DialogHeader className="border-b border-slate-200 p-5 pr-12 text-left sm:p-6 sm:pr-12">
          <DialogTitle className="text-xl leading-7 [overflow-wrap:anywhere]">
            Internal notes{candidate.name ? ` — ${candidate.name}` : ""}
          </DialogTitle>
          <DialogDescription className="text-sm leading-6 text-slate-600">
            Visible to your team only. The candidate never sees these notes.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 min-w-0 overflow-y-auto p-3 sm:p-5">
          <CandidateNotesThread
            organizationBusinessUnitId={organizationBusinessUnitId}
            userId={candidate.userId}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
